import { fetchAuth } from "../../../shared/api/ApiService"
import { IPenaltyBonusItem, IPenaltyBonusSummary, IStaffPenaltyBonusItem, IStaffPenaltiesBonusesSummary, TPenaltyBonus } from "../model/types"

const readValue = (data: Record<string, unknown>, ...keys: string[]) => {
    for(const key of keys){
        if(data[key] !== undefined && data[key] !== null) return String(data[key])
    }
    return ''
}

const readUserId = (data: Record<string, unknown>, ...keys: string[]) => {
    const value = readValue(data, ...keys)
    const id = Number(value)
    return Number.isFinite(id) && id > 0 ? id : undefined
}

const readCreator = (data: Record<string, unknown>) => {
    const creator = data.created_by ?? data.createdBy
    if(creator && typeof creator === 'object'){
        const creatorData = creator as Record<string, unknown>
        return {
            id: readUserId(creatorData, 'user_id', 'userId', 'id'),
            name: readValue(creatorData, 'full_name', 'fullName', 'name', 'user_name', 'userName'),
        }
    }

    const creatorIsId = typeof creator === 'number' || (typeof creator === 'string' && /^\d+$/.test(creator))
    const id = readUserId(data, 'created_by_user_id', 'createdByUserId', 'created_by_id', 'createdById')
        || (creatorIsId
            ? readUserId({creator}, 'creator')
            : undefined)

    return {
        id,
        name: creatorIsId ? '' : readValue(data, 'created_by_name', 'createdByName', 'created_by', 'createdBy'),
    }
}

const mapPenaltyBonusItem = (item: unknown): IPenaltyBonusItem => {
    const data = item as Record<string, unknown>
    const creator = readCreator(data)

    return {
        id: readValue(data, 'id'),
        actualAt: readValue(data, 'actual_at', 'actualAt'),
        createdAt: readValue(data, 'created_at', 'createdAt') || undefined,
        userId: readUserId(data, 'user_id', 'userId', 'target_user_id', 'targetUserId'),
        userName: readValue(data, 'user_name', 'userName') || undefined,
        type: readValue(data, 'type').toLowerCase().includes('penalty') ? 'penalty' : 'bonus',
        amount: readValue(data, 'amount'),
        comment: readValue(data, 'comment'),
        createdByUserId: creator.id,
        createdByName: creator.name || undefined,
        isPaid: data.is_paid !== undefined || data.isPaid !== undefined
            ? data.is_paid === true || data.isPaid === true || data.is_paid === 'true' || data.isPaid === 'true'
            : undefined,
    }
}

const mapPenaltyBonusSummary = (summary: unknown): IPenaltyBonusSummary => {
    const data = (summary || {}) as Record<string, unknown>
    return {
        penalties: readValue(data, 'penalties'),
        bonuses: readValue(data, 'bonuses'),
    }
}

const mapStaffItem = (item: unknown): IStaffPenaltyBonusItem => {
    const data = item as Record<string, unknown>
    const creator = readCreator(data)
    return {
        id: readValue(data, 'id'),
        actual_at: readValue(data, 'actual_at', 'actualAt'),
        created_at: readValue(data, 'created_at', 'createdAt'),
        user_id: readUserId(data, 'user_id', 'userId', 'target_user_id', 'targetUserId'),
        user_name: readValue(data, 'user_name', 'userName'),
        type: readValue(data, 'type').toLowerCase().includes('penalty') ? 'penalty' : 'bonus',
        amount: readValue(data, 'amount'),
        comment: readValue(data, 'comment'),
        created_by_user_id: creator.id,
        created_by_name: creator.name,
        is_paid: data.is_paid === true || data.isPaid === true || data.is_paid === 'true' || data.isPaid === 'true',
    }
}

const mapStaffSummary = (summary: unknown): IStaffPenaltiesBonusesSummary => {
    const data = (summary || {}) as Record<string, unknown>
    return {
        penalties: readValue(data, 'penalties'),
        bonuses: readValue(data, 'bonuses'),
        to_pay: readValue(data, 'to_pay', 'toPay'),
        unpaid: readValue(data, 'unpaid'),
    }
}

class PenaltyBonusService {

    async userGetPenaltiesBonuses(userId: number, from: string, to: string) {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${userId}/penalties-bonuses/list`, {
            method: "POST",
            body: JSON.stringify({from, to}),
        })
        const data = await res.json() as {items?: unknown[]; summary?: unknown}
        return {
            items: (data.items || []).map(mapPenaltyBonusItem),
            summary: mapPenaltyBonusSummary(data.summary),
        }
    }
    
    async userAddPenaltiesBonuses(userId: number, amount: string, comment: string, type: TPenaltyBonus, actualDate: string) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${userId}/penalties-bonuses`, {
            method: "POST",
            body: JSON.stringify({amount, comment, type, actualDate}),
        })
    }

    async userDeletePenaltiesBonuses(userId: number, penaltyBonusId: number) {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/billing/users/${userId}/penalties-bonuses/${penaltyBonusId}/delete`, {
            method: "POST",
        })
    }

    async tutorGetPenaltiesBonuses(tutorId: number, from: string, to: string) {
        return this.userGetPenaltiesBonuses(tutorId, from, to)
    }
    
    async tutorAddPenaltiesBonuses(tutorId: number, amount: string, comment: string, type: TPenaltyBonus, actualDate: string) {
        return this.userAddPenaltiesBonuses(tutorId, amount, comment, type, actualDate)
    }

    async tutorDeletePenaltiesBonuses(tutorId: number, penaltyBonusId: number) {
        return this.userDeletePenaltiesBonuses(tutorId, penaltyBonusId)
    }

    async getStaffPenaltiesBonuses(from: string, to: string): Promise<{items: IStaffPenaltyBonusItem[]; summary: IStaffPenaltiesBonusesSummary}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL + '/api/v1/billing/penalties-bonuses/list', {
            method: 'POST',
            body: JSON.stringify({from, to}),
        })
        const data = await res.json() as {items?: unknown[]; summary?: unknown}
        return {
            items: (data.items || []).map(mapStaffItem),
            summary: mapStaffSummary(data.summary),
        }
    }

}

export const penaltyBonusService = new PenaltyBonusService()
